import { useState } from "react";
import { motion } from "framer-motion";
import { Waves, LogIn, Loader2, Mic, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { errMsg } from "@/api";
import { APP_NAME, APP_TAGLINE } from "@/brand";

const BARS = [0.5, 0.9, 0.35, 1, 0.6, 0.85, 0.4, 0.95, 0.55, 0.75, 0.45, 0.8];

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      toast.error(errMsg(err, "Error al iniciar sesión"));
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="relative min-h-screen overflow-hidden bg-brand-navy pt-10 flex items-center justify-center px-5 py-12">
      <div className="pointer-events-none absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-brand-teal/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-brand-teal/10 blur-3xl" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-teal shadow-lg shadow-brand-teal/30 mb-5">
            <Mic className="w-8 h-8 text-white" />
          </div>
          <div className="flex items-end justify-center gap-1 h-8 mb-5" aria-hidden="true">
            {BARS.map((h, i) => (
              <span key={i} className="eq-bar w-1.5 rounded-full bg-brand-teal/80" style={{ height: `${h * 100}%`, animationDelay: `${i * 0.09}s` }} />
            ))}
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white" data-testid="app-name">{APP_NAME}</h1>
          <p className="mt-3 text-white/70 text-base max-w-xs mx-auto">{APP_TAGLINE}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-7 sm:p-8">
          <div className="flex items-center gap-2 text-brand-teal text-xs font-bold uppercase tracking-[0.2em] mb-5">
            <Waves className="w-4 h-4" /> Acceso por clínica
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-brand-navy">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" autoCapitalize="none" placeholder="tu@clinica.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-brand-navy">Contraseña</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" />
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white h-12 mt-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Iniciar sesión
            </Button>
          </form>

          <div className="mt-5 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-2">Cuentas demo (pulsa para rellenar):</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="demo-therapist" onClick={() => quick("fisio@revida.com", "demo123")} className="text-xs px-3 py-1.5 rounded-full bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/20">Terapeuta</button>
              <button type="button" data-testid="demo-clinic-admin" onClick={() => quick("admin@revida.com", "admin123")} className="text-xs px-3 py-1.5 rounded-full bg-brand-navy/10 text-brand-navy hover:bg-brand-navy/20">Admin clínica</button>
              <button type="button" data-testid="demo-super-admin" onClick={() => quick("super@notaviva.com", "super123")} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200">Super-admin</button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-white/50 text-xs mt-5">
          <ShieldCheck className="w-3.5 h-3.5" /> El audio se elimina automáticamente tras transcribir.
        </div>
      </motion.div>
    </div>
  );
}
